/* eslint react-hooks/exhaustive-deps: "warn" */

import { useEffect, useRef, useState } from 'react';

import cn from 'classnames';

import { useInt } from 'hooks/useInt';
import { useIntersection } from 'hooks/useIntersection';

import { Icon } from 'ui/atoms/Icon/Icon';
import { Section } from 'ui/molecules/Section/Section';

import styles from './CompanyDataProvenance.module.scss';

type Translate = (key: string) => string;

const getStages = (t: Translate) =>
  [
    { num: '1', label: t('Agent answer'), title: t('The sentence') },
    { num: '2', label: t('Field in the response'), title: t('The record') },
    { num: '3', label: t('Filing at the register'), title: t('The document') },
  ] as const;

const STAGE_COUNT = 3;
const STEP_DELAY = 850;
const RESET_DELAY = 900;

export const CompanyDataProvenance = () => {
  const { t } = useInt();
  const STAGES = getStages(t);
  const chainRef = useRef<HTMLDivElement>(null);
  const played = useIntersection(chainRef, { threshold: 0.45 });

  const [activeStage, setActiveStage] = useState(-1);

  useEffect(() => {
    if (!played || (typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches)) {
      return undefined;
    }

    const timeouts: ReturnType<typeof setTimeout>[] = [];
    let i = 0;

    const step = () => {
      if (i >= STAGE_COUNT) {
        timeouts.push(setTimeout(() => setActiveStage(-1), RESET_DELAY));
        return;
      }

      setActiveStage(i);
      i += 1;
      timeouts.push(setTimeout(step, STEP_DELAY));
    };

    step();

    return () => timeouts.forEach(clearTimeout);
  }, [played]);

  return (
    <Section
      className={styles.root}
      title={t('Data provenance, walked end to end.')}
      description={t(
        'Provenance is only useful if a reviewer can follow it from the sentence the agent wrote to the document the register holds. This is the walk, using our own record.',
      )}
    >
      <div ref={chainRef} className={styles.chain}>
        {STAGES.map((stage, i) => (
          <div
            key={stage.num}
            className={cn(styles.stage, i === activeStage && styles.stageActive)}
            onClick={() => setActiveStage(i)}
          >
            <div className={styles.stageHead}>
              <span className={styles.stageNum}>{stage.num}</span>
              <span className={styles.stageLabel}>{stage.label}</span>
            </div>

            <h3 className={styles.stageTitle}>{stage.title}</h3>

            {i === 0 && (
              <>
                <p className={styles.stageDesc}>
                  {t(
                    'What the user or the downstream system sees. Each value carries a marker back to the source list.',
                  )}
                </p>
                <div className={styles.artChat}>
                  <span className={styles.chatAv} aria-hidden="true">
                    <span className={styles.chatAvDot} />
                  </span>
                  <div className={styles.chatBubble}>
                    Incorporated<sup>2</sup> <span className={styles.mono}>28 January 2015</span>. Registered office
                    <sup>3</sup>{' '}
                    <span className={styles.mono}>Artisans&apos; House, 7 Queensbridge, Northampton NN4 7BF</span>.
                  </div>
                </div>
              </>
            )}

            {i === 1 && (
              <>
                <p className={styles.stageDesc}>
                  {t(
                    'The structured entry the marker points at: field, value, register, filing reference, time of read.',
                  )}
                </p>
                <div className={styles.artRecord}>
                  <div className={styles.recordHead}>
                    Source record <em>sources[2]</em>
                  </div>
                  <div className={styles.recordRow}>
                    <i>Field</i>
                    <b>Incorporation date</b>
                  </div>
                  <div className={styles.recordRow}>
                    <i>Value</i>
                    <b>28 January 2015</b>
                  </div>
                  <div className={styles.recordRow}>
                    <i>Register</i>
                    <b>Companies House, GB</b>
                  </div>
                  <div className={styles.recordRow}>
                    <i>Filing</i>
                    <b className={styles.mono}>IN01</b>
                  </div>
                  <div className={styles.recordRow}>
                    <i>Retrieved</i>
                    <b className={styles.mono}>2026-09-01T08:14:02Z</b>
                  </div>
                </div>
              </>
            )}

            {i === 2 && (
              <>
                <p className={styles.stageDesc}>
                  {t('The statutory filing itself, at the government register, opened independently of us.')}
                </p>
                <div className={styles.artDoc}>
                  <div className={styles.docHead}>
                    <Icon url="/storage/flags/gb.svg" className={styles.flag} />
                    Companies House
                  </div>
                  <div className={styles.docBody}>
                    GLOBAL DATA INTELLIGENCE LIMITED
                    <br />
                    <span className={styles.docNum}>Company number 09410808</span>
                  </div>
                  <a
                    className={styles.docOpen}
                    href="https://find-and-update.company-information.service.gov.uk/company/09410808"
                    rel="noopener noreferrer"
                    target="_blank"
                  >
                    Open at Companies House
                    <svg viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true">
                      <path d="M4.5 2.5h5v5M9.5 2.5 3 9" />
                    </svg>
                  </a>
                </div>
              </>
            )}
          </div>
        ))}
      </div>

      <div className={styles.notes}>
        <div className={cn(styles.note, styles.noteGap)}>
          <span className={styles.noteIcon} aria-hidden="true">
            <svg viewBox="0 0 20 20" fill="none">
              <path d="M10 2 18 16H2L10 2Z" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" />
              <path d="M10 8v3.5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
              <circle cx="10" cy="14" r="0.9" fill="currentColor" />
            </svg>
          </span>
          <div>
            <h4>{t('When the register has no field')}</h4>
            <p>
              {t(
                'Some registers publish no financial statements, no shareholders or no beneficial owners for a given entity type. The response says so and names the register.',
              )}{' '}
              <strong>
                {t('A gap with a stated reason is a defensible entry in a case file. An estimate is not.')}
              </strong>
            </p>
          </div>
        </div>

        <div className={styles.note}>
          <span className={styles.noteIcon} aria-hidden="true">
            <svg viewBox="0 0 20 20" fill="none">
              <circle cx="10" cy="10" r="7.3" stroke="currentColor" strokeWidth="1.6" />
              <path
                d="M10 6v4.2l3 1.8"
                stroke="currentColor"
                strokeWidth="1.6"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </span>
          <div>
            <h4>{t('Freshness')}</h4>
            <p>
              {t(
                "Registers publish on their own schedules, from continuous to annual. Records are synced daily, and the retrieval timestamp on each value tells the agent, and the reviewer, which side of a register's update the read landed on.",
              )}
            </p>
          </div>
        </div>
      </div>
    </Section>
  );
};
